import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Subcategories } from '../../../ui/navigation/Subcategories/Subcategories';
import { Subcategory } from '../../../ui/navigation/Subcategories/styles';
import { Tab, Tabs } from '../../../ui/navigation/Tabs';
import { canViewBusinessAsAdmin, isBloggerBusiness } from '../../../utils/accessService';
import { useIsMobile } from '../../../utils/getIsMobile';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useTranslation } from '../../../utils/useTranslation';
import { useCurrentUser } from '../../users/useCurrentUser';
import { useBusinessModel } from '../useBusinessModel';
import { useBloggerTabRedirects } from './hokks/useTabRedirects';
import { TabText } from './styles';
import { BusinessDetailsTabsProps } from './types';
import {
    buildBloggerTabs,
    buildBloggerTabsWideProfile,
    buildSubcategories,
} from './utils/buildBloggerTabs';
import { buildBusinessTabs } from './utils/buildBusinessTabs';
import { getTabsValue } from './utils/getTabsValue';

export const BusinessDetailsTabs = ({ onTabClick, ...other }: BusinessDetailsTabsProps) => {
    const params = useParams();

    const t = useTranslation();

    const businessId = params.businessId!;
    const business = useBusinessModel({ businessId });

    const [query] = useSearchParams();
    const { pathname } = useLocation();
    const currentUser = useCurrentUser();

    const isMobile = useIsMobile();
    const isWideProfileExp = Number(useExperimentFlag('rythm_disable_wide_profile')) !== 1;
    const isWideProfile = isWideProfileExp && !isMobile;

    const { tabsValue, subcategoriesValue } = getTabsValue(pathname, query, isWideProfile);
    const { tabsHidden, subcategoriesHidden, hasSavedCollections } =
        useBloggerTabRedirects(tabsValue);
    const isAdmin = canViewBusinessAsAdmin(business?.id, currentUser);

    const hasCollections = isAdmin && !subcategoriesHidden;

    const isBlogger = isBloggerBusiness(business?.type);

    if (!isAdmin && !isBlogger) {
        return null;
    }

    if (isBlogger) {
        const bloggerTabs = isWideProfile
            ? buildBloggerTabsWideProfile(businessId, isAdmin, hasSavedCollections, tabsHidden)
            : buildBloggerTabs(businessId, tabsHidden);

        return (
            <>
                {bloggerTabs.length ? (
                    <Tabs px={{ xs: 1, sm: 3 }} value={tabsValue} onChange={onTabClick} {...other}>
                        {bloggerTabs.map((tabItem) => (
                            <Tab
                                key={tabItem.value}
                                as={Link}
                                width={isWideProfile ? 'auto' : '50%'}
                                replace
                                to={tabItem.link}
                                value={tabItem.value}
                                textDecoration="none"
                                px={isWideProfile ? 2 : 1}
                                py={isWideProfile ? 2 : 1}
                            >
                                <TabText
                                    textTransform={isWideProfile ? 'uppercase' : 'none'}
                                    variant={isWideProfile ? 'caption' : 'body'}
                                    weight={isWideProfile ? 'bold' : 'regular'}
                                    letterSpacing={isWideProfile ? '0.7px' : 'normal'}
                                    color={
                                        tabItem.value === pathname || !isWideProfile
                                            ? 'var(--color-primary)'
                                            : 'var(--color-secondary)'
                                    }
                                >
                                    {t(tabItem.label)}
                                </TabText>
                            </Tab>
                        ))}
                    </Tabs>
                ) : null}
                {hasCollections && !isWideProfile ? (
                    <Subcategories
                        p={2}
                        justifySelf={'center'}
                        value={subcategoriesValue}
                        borderBottom={isWideProfile ? 'none' : '1px solid var(--divider)'}
                    >
                        {buildSubcategories(businessId, tabsValue).map((subcategory, i) => (
                            <Subcategory
                                key={subcategory.value}
                                ml={i === 0 ? undefined : 1}
                                as={Link}
                                replace
                                to={subcategory.link}
                                value={subcategory.value}
                                textDecoration="none"
                                size="m"
                            >
                                {t(subcategory.label)}
                            </Subcategory>
                        ))}
                    </Subcategories>
                ) : null}
            </>
        );
    }

    return (
        <Tabs px={{ xs: 1, sm: 3 }} value={pathname} onChange={onTabClick} {...other}>
            {buildBusinessTabs(businessId, t).map((tabItem) => (
                <Tab
                    key={tabItem.value}
                    as={Link}
                    width={isWideProfile ? 'auto' : '50%'}
                    replace
                    to={tabItem.value}
                    value={tabItem.value}
                    textDecoration="none"
                    px={isWideProfile ? 2 : 1}
                    py={isWideProfile ? 2 : 1}
                >
                    <TabText
                        textTransform={isWideProfile ? 'uppercase' : 'none'}
                        variant={isWideProfile ? 'caption' : 'body'}
                        weight={isWideProfile ? 'bold' : 'regular'}
                        letterSpacing={isWideProfile ? '0.7px' : 'normal'}
                        color={
                            tabItem.value === pathname || !isWideProfile
                                ? 'var(--color-primary)'
                                : 'var(--color-secondary)'
                        }
                    >
                        {tabItem.label}
                    </TabText>
                </Tab>
            ))}
        </Tabs>
    );
};
